<script lang="ts">
  /**
   * Checkboxes for which task fields the "Copy incomplete as…" actions include.
   * Changes save immediately to the user's preferences. "Details" is a parent
   * toggle for Priority / Created / Last Updated; its checked/indeterminate state
   * is derived from those three, so only the individual fields are stored.
   */
  import { taskCopyPreferences, setTaskCopyPreferences } from '../lib/stores/taskCopyPreferences';
  import {
    TASK_COPY_DETAIL_FIELDS,
    TASK_COPY_FIELD_LABELS,
    type TaskCopyField,
  } from '../lib/taskClipboard';

  const TOP_LEVEL_FIELDS: TaskCopyField[] = ['description', 'tags'];

  let prefs = $derived($taskCopyPreferences);
  let detailsOnCount = $derived(TASK_COPY_DETAIL_FIELDS.filter((field) => prefs[field]).length);
  let allDetails = $derived(detailsOnCount === TASK_COPY_DETAIL_FIELDS.length);
  let someDetails = $derived(detailsOnCount > 0 && !allDetails);

  function toggleField(field: TaskCopyField) {
    void setTaskCopyPreferences({ [field]: !prefs[field] });
  }

  // Partially-on or all-off turns every detail on; all-on turns them off.
  function toggleDetails() {
    const next = !allDetails;
    void setTaskCopyPreferences(
      Object.fromEntries(TASK_COPY_DETAIL_FIELDS.map((field) => [field, next])),
    );
  }

  // `indeterminate` is a DOM property with no HTML attribute.
  function indeterminate(value: boolean) {
    return (node: HTMLInputElement) => {
      node.indeterminate = value;
    };
  }
</script>

<fieldset class="copy-fields">
  <legend class="sr-only">Include in copied tasks</legend>

  {#each TOP_LEVEL_FIELDS as field (field)}
    <label class="field">
      <input type="checkbox" checked={prefs[field]} onchange={() => toggleField(field)} />
      {TASK_COPY_FIELD_LABELS[field]}
    </label>
  {/each}

  <label class="field">
    <input
      type="checkbox"
      checked={allDetails}
      onchange={toggleDetails}
      {@attach indeterminate(someDetails)}
    />
    Details
  </label>
  <div class="nested">
    {#each TASK_COPY_DETAIL_FIELDS as field (field)}
      <label class="field">
        <input type="checkbox" checked={prefs[field]} onchange={() => toggleField(field)} />
        {TASK_COPY_FIELD_LABELS[field]}
      </label>
    {/each}
  </div>
</fieldset>

<style>
  .copy-fields {
    margin: 0;
    padding: 0;
    border: none;
    min-width: 0;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .field {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.375rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #374151;
    border-radius: 0.3125rem;
    cursor: pointer;
    user-select: none;
  }

  .field:hover {
    background: #f3f4f6;
  }

  .field input {
    width: 0.75rem;
    height: 0.75rem;
    margin: 0;
    accent-color: #2563eb;
    cursor: pointer;
  }

  .nested {
    margin-left: 1.125rem;
  }
</style>
